// Servicio para parámetros del sistema - conecta con el Mock API
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// ============================================
// TIPOS DE MOVIMIENTO (MovementTypes)
// ============================================

export interface MovementTypeAPI {
  id: string;
  code: string;
  name: string;
  description: string;
  direction: 'entry' | 'exit';
  requiresApproval: boolean;
  isActive: boolean;
  category: string;
  orderIndex: number;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface MovementTypeCreateData {
  code: string;
  name: string;
  description?: string;
  direction: 'entry' | 'exit';
  requiresApproval?: boolean;
  category?: string;
  orderIndex?: number;
}

export const movementTypesService = {
  // Obtener todos los tipos de movimiento
  async getAll(params?: { direction?: 'entry' | 'exit'; includeInactive?: boolean }): Promise<MovementTypeAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.direction) queryParams.set('direction', params.direction);
    if (params?.includeInactive) queryParams.set('includeInactive', 'true');

    const url = `${API_BASE_URL}/movement-types${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener tipos de movimiento');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener un tipo de movimiento por ID
  async getById(id: string): Promise<MovementTypeAPI> {
    const response = await authFetch(`${API_BASE_URL}/movement-types/${id}`);

    if (!response.ok) {
      throw new Error('Tipo de movimiento no encontrado');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nuevo tipo de movimiento
  async create(movementType: MovementTypeCreateData): Promise<MovementTypeAPI> {
    const response = await authFetch(`${API_BASE_URL}/movement-types`, {
      method: 'POST',
      body: JSON.stringify(movementType)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear tipo de movimiento');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar tipo de movimiento
  async update(id: string, movementType: Partial<MovementTypeCreateData>): Promise<MovementTypeAPI> {
    const response = await authFetch(`${API_BASE_URL}/movement-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(movementType)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar tipo de movimiento');
    }

    const data = await response.json();
    return data.data;
  },

  // Cambiar estado de tipo de movimiento
  async toggleStatus(id: string, isActive: boolean): Promise<MovementTypeAPI> {
    const response = await authFetch(`${API_BASE_URL}/movement-types/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar tipo de movimiento (soft delete)
  async delete(id: string): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/movement-types/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al eliminar tipo de movimiento');
    }
  }
};

// ============================================
// PRESENTACIONES (Presentations)
// ============================================

export interface PresentationAPI {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PresentationCreateData {
  name: string;
  description?: string;
}

export const presentationsService = {
  // Obtener todas las presentaciones
  async getAll(params?: { search?: string; isActive?: boolean | 'all' }): Promise<PresentationAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive === 'all' ? 'all' : String(params.isActive));

    const url = `${API_BASE_URL}/presentations${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener presentaciones');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener una presentación por ID
  async getById(id: number): Promise<PresentationAPI> {
    const response = await authFetch(`${API_BASE_URL}/presentations/${id}`);

    if (!response.ok) {
      throw new Error('Presentación no encontrada');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nueva presentación
  async create(presentation: PresentationCreateData): Promise<PresentationAPI> {
    const response = await authFetch(`${API_BASE_URL}/presentations`, {
      method: 'POST',
      body: JSON.stringify(presentation)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear presentación');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar presentación
  async update(id: number, presentation: Partial<PresentationCreateData>): Promise<PresentationAPI> {
    const response = await authFetch(`${API_BASE_URL}/presentations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(presentation)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar presentación');
    }

    const data = await response.json();
    return data.data;
  },

  // Toggle estado de presentación
  async toggle(id: number): Promise<PresentationAPI> {
    const response = await authFetch(`${API_BASE_URL}/presentations/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar presentación (soft delete)
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/presentations/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al eliminar presentación');
    }
  }
};

// ============================================
// CATEGORÍAS DE PRODUCTOS (ProductCategories)
// ============================================

export interface ProductCategoryAPI {
  id: number;
  code: string;
  name: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductCategoryCreateData {
  code: string;
  name: string;
  description?: string;
  displayOrder?: number;
}

export const productCategoriesService = {
  // Obtener todas las categorías
  async getAll(params?: { search?: string; isActive?: boolean | 'all' }): Promise<ProductCategoryAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive === 'all' ? 'all' : String(params.isActive));

    const url = `${API_BASE_URL}/categories${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener categorías');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener una categoría por ID
  async getById(id: number): Promise<ProductCategoryAPI> {
    const response = await authFetch(`${API_BASE_URL}/categories/${id}`);

    if (!response.ok) {
      throw new Error('Categoría no encontrada');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nueva categoría
  async create(category: ProductCategoryCreateData): Promise<ProductCategoryAPI> {
    const response = await authFetch(`${API_BASE_URL}/categories`, {
      method: 'POST',
      body: JSON.stringify(category)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear categoría');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar categoría
  async update(id: number, category: Partial<ProductCategoryCreateData>): Promise<ProductCategoryAPI> {
    const response = await authFetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(category)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar categoría');
    }

    const data = await response.json();
    return data.data;
  },

  // Toggle estado de categoría
  async toggle(id: number): Promise<ProductCategoryAPI> {
    const response = await authFetch(`${API_BASE_URL}/categories/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar categoría
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al eliminar categoría');
    }
  }
};

// ============================================
// CARGOS/POSICIONES (Positions)
// ============================================

export interface PositionAPI {
  id: number;
  name: string;
  code: string;
  description: string;
  level: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PositionCreateData {
  name: string;
  code: string;
  description?: string;
  level: string;
}

export const positionsService = {
  // Obtener todas las posiciones
  async getAll(params?: { search?: string; level?: string; isActive?: boolean | 'all' }): Promise<PositionAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.level) queryParams.set('level', params.level);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive === 'all' ? 'all' : String(params.isActive));

    const url = `${API_BASE_URL}/positions${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener posiciones');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener niveles disponibles
  async getLevels(): Promise<{ value: string; label: string; description: string }[]> {
    const response = await authFetch(`${API_BASE_URL}/positions/levels`);

    if (!response.ok) {
      throw new Error('Error al obtener niveles');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener una posición por ID
  async getById(id: number): Promise<PositionAPI> {
    const response = await authFetch(`${API_BASE_URL}/positions/${id}`);

    if (!response.ok) {
      throw new Error('Posición no encontrada');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nueva posición
  async create(position: PositionCreateData): Promise<PositionAPI> {
    const response = await authFetch(`${API_BASE_URL}/positions`, {
      method: 'POST',
      body: JSON.stringify(position)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear posición');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar posición
  async update(id: number, position: Partial<PositionCreateData>): Promise<PositionAPI> {
    const response = await authFetch(`${API_BASE_URL}/positions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(position)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar posición');
    }

    const data = await response.json();
    return data.data;
  },

  // Toggle estado de posición
  async toggle(id: number): Promise<PositionAPI> {
    const response = await authFetch(`${API_BASE_URL}/positions/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar posición (soft delete)
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/positions/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al desactivar posición');
    }
  }
};

// ============================================
// CONDUCTORES (Drivers)
// ============================================

export interface DriverAPI {
  id: number;
  fullName: string;
  licenseNumber: string;
  licenseType: string;
  licenseExpiry: string;
  dni: string;
  phone: string;
  email: string;
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DriverCreateData {
  fullName: string;
  licenseNumber: string;
  licenseType: string;
  licenseExpiry: string;
  dni?: string;
  phone?: string;
  email?: string;
  address?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
}

export const driversService = {
  // Obtener todos los conductores
  async getAll(params?: { search?: string; licenseType?: string; isActive?: boolean | 'all' }): Promise<DriverAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.licenseType) queryParams.set('licenseType', params.licenseType);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive === 'all' ? 'all' : String(params.isActive));

    const url = `${API_BASE_URL}/drivers${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener conductores');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener tipos de licencia
  async getLicenseTypes(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch(`${API_BASE_URL}/drivers/license-types`);

    if (!response.ok) {
      throw new Error('Error al obtener tipos de licencia');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener un conductor por ID
  async getById(id: number): Promise<DriverAPI> {
    const response = await authFetch(`${API_BASE_URL}/drivers/${id}`);

    if (!response.ok) {
      throw new Error('Conductor no encontrado');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nuevo conductor
  async create(driver: DriverCreateData): Promise<DriverAPI> {
    const response = await authFetch(`${API_BASE_URL}/drivers`, {
      method: 'POST',
      body: JSON.stringify(driver)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear conductor');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar conductor
  async update(id: number, driver: Partial<DriverCreateData>): Promise<DriverAPI> {
    const response = await authFetch(`${API_BASE_URL}/drivers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(driver)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar conductor');
    }

    const data = await response.json();
    return data.data;
  },

  // Toggle estado de conductor
  async toggle(id: number): Promise<DriverAPI> {
    const response = await authFetch(`${API_BASE_URL}/drivers/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar conductor (soft delete)
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/drivers/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al desactivar conductor');
    }
  }
};

// ============================================
// VEHÍCULOS (Vehicles)
// ============================================

export interface VehicleAPI {
  id: number;
  plate: string;
  brand: string;
  model: string;
  year: number;
  color: string;
  engineNumber: string;
  chassisNumber: string;
  registrationExpiry: string;
  insuranceExpiry: string;
  technicalReviewExpiry: string;
  vehicleType: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleCreateData {
  plate: string;
  brand: string;
  model: string;
  year: number;
  color?: string;
  engineNumber?: string;
  chassisNumber?: string;
  registrationExpiry?: string;
  insuranceExpiry?: string;
  technicalReviewExpiry?: string;
  vehicleType?: string;
}

export const vehiclesService = {
  // Obtener todos los vehículos
  async getAll(params?: { search?: string; vehicleType?: string; isActive?: boolean | 'all' }): Promise<VehicleAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.vehicleType) queryParams.set('vehicleType', params.vehicleType);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive === 'all' ? 'all' : String(params.isActive));

    const url = `${API_BASE_URL}/vehicles${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener vehículos');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener tipos de vehículo
  async getVehicleTypes(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch(`${API_BASE_URL}/vehicles/types`);

    if (!response.ok) {
      throw new Error('Error al obtener tipos de vehículo');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener un vehículo por ID
  async getById(id: number): Promise<VehicleAPI> {
    const response = await authFetch(`${API_BASE_URL}/vehicles/${id}`);

    if (!response.ok) {
      throw new Error('Vehículo no encontrado');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nuevo vehículo
  async create(vehicle: VehicleCreateData): Promise<VehicleAPI> {
    const response = await authFetch(`${API_BASE_URL}/vehicles`, {
      method: 'POST',
      body: JSON.stringify(vehicle)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear vehículo');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar vehículo
  async update(id: number, vehicle: Partial<VehicleCreateData>): Promise<VehicleAPI> {
    const response = await authFetch(`${API_BASE_URL}/vehicles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(vehicle)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar vehículo');
    }

    const data = await response.json();
    return data.data;
  },

  // Toggle estado de vehículo
  async toggle(id: number): Promise<VehicleAPI> {
    const response = await authFetch(`${API_BASE_URL}/vehicles/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar vehículo (soft delete)
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/vehicles/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al desactivar vehículo');
    }
  }
};

// ============================================
// EMPRESAS DE TRANSPORTE (TransportCompanies)
// ============================================

export interface TransportCompanyAPI {
  id: number;
  companyName: string;
  ruc: string;
  address: string;
  phone: string;
  email: string;
  contactPerson: string;
  contactPhone: string;
  mtcAuthorization: string;
  authorizationExpiry: string;
  serviceTypes: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TransportCompanyCreateData {
  companyName: string;
  ruc: string;
  address: string;
  phone: string;
  contactPerson: string;
  contactPhone: string;
  email?: string;
  mtcAuthorization?: string;
  authorizationExpiry?: string;
  serviceTypes?: string[];
}

export const transportCompaniesService = {
  // Obtener todas las empresas de transporte
  async getAll(params?: { search?: string; isActive?: boolean | 'all' }): Promise<TransportCompanyAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive === 'all' ? 'all' : String(params.isActive));

    const url = `${API_BASE_URL}/transport-companies${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener empresas de transporte');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener tipos de servicio
  async getServiceTypes(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch(`${API_BASE_URL}/transport-companies/service-types`);

    if (!response.ok) {
      throw new Error('Error al obtener tipos de servicio');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener una empresa por ID
  async getById(id: number): Promise<TransportCompanyAPI> {
    const response = await authFetch(`${API_BASE_URL}/transport-companies/${id}`);

    if (!response.ok) {
      throw new Error('Empresa de transporte no encontrada');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nueva empresa de transporte
  async create(company: TransportCompanyCreateData): Promise<TransportCompanyAPI> {
    const response = await authFetch(`${API_BASE_URL}/transport-companies`, {
      method: 'POST',
      body: JSON.stringify(company)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear empresa de transporte');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar empresa de transporte
  async update(id: number, company: Partial<TransportCompanyCreateData>): Promise<TransportCompanyAPI> {
    const response = await authFetch(`${API_BASE_URL}/transport-companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(company)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar empresa de transporte');
    }

    const data = await response.json();
    return data.data;
  },

  // Toggle estado de empresa de transporte
  async toggle(id: number): Promise<TransportCompanyAPI> {
    const response = await authFetch(`${API_BASE_URL}/transport-companies/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar empresa de transporte (soft delete)
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/transport-companies/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al desactivar empresa de transporte');
    }
  }
};

// ============================================
// PARÁMETROS FISCALES (FiscalParameters)
// ============================================

export interface FiscalParameterAPI {
  id: number;
  name: string;
  code: string;
  value: number;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FiscalParameterCreateData {
  name: string;
  code: string;
  value: number;
  description?: string;
}

export const fiscalParametersService = {
  // Obtener todos los parámetros fiscales
  async getAll(): Promise<FiscalParameterAPI[]> {
    const response = await authFetch(`${API_BASE_URL}/fiscal-parameters`);

    if (!response.ok) {
      throw new Error('Error al obtener parámetros fiscales');
    }

    return await response.json();
  },

  // Obtener parámetros fiscales activos
  async getActive(): Promise<FiscalParameterAPI[]> {
    const response = await authFetch(`${API_BASE_URL}/fiscal-parameters/active`);

    if (!response.ok) {
      throw new Error('Error al obtener parámetros fiscales activos');
    }

    return await response.json();
  },

  // Obtener un parámetro fiscal por ID
  async getById(id: number): Promise<FiscalParameterAPI> {
    const response = await authFetch(`${API_BASE_URL}/fiscal-parameters/${id}`);

    if (!response.ok) {
      throw new Error('Parámetro fiscal no encontrado');
    }

    return await response.json();
  },

  // Crear nuevo parámetro fiscal
  async create(parameter: FiscalParameterCreateData): Promise<FiscalParameterAPI> {
    const response = await authFetch(`${API_BASE_URL}/fiscal-parameters`, {
      method: 'POST',
      body: JSON.stringify(parameter)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear parámetro fiscal');
    }

    return await response.json();
  },

  // Actualizar parámetro fiscal
  async update(id: number, parameter: Partial<FiscalParameterCreateData>): Promise<FiscalParameterAPI> {
    const response = await authFetch(`${API_BASE_URL}/fiscal-parameters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(parameter)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar parámetro fiscal');
    }

    return await response.json();
  },

  // Toggle estado de parámetro fiscal
  async toggle(id: number): Promise<FiscalParameterAPI> {
    const response = await authFetch(`${API_BASE_URL}/fiscal-parameters/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.parameter;
  },

  // Eliminar parámetro fiscal (soft delete)
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/fiscal-parameters/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al desactivar parámetro fiscal');
    }
  }
};

// ============================================
// CATEGORÍAS DE INSUMOS (IngredientCategories)
// ============================================

export interface IngredientCategoryAPI {
  id: number;
  name: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IngredientCategoryCreateData {
  name: string;
  description?: string;
  displayOrder?: number;
}

export const ingredientCategoriesService = {
  // Obtener todas las categorías de insumos
  async getAll(params?: { search?: string; isActive?: boolean | 'all' }): Promise<IngredientCategoryAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive === 'all' ? 'all' : String(params.isActive));

    const url = `${API_BASE_URL}/ingredient-categories${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener categorías de insumos');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener una categoría de insumo por ID
  async getById(id: number): Promise<IngredientCategoryAPI> {
    const response = await authFetch(`${API_BASE_URL}/ingredient-categories/${id}`);

    if (!response.ok) {
      throw new Error('Categoría de insumo no encontrada');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nueva categoría de insumo
  async create(category: IngredientCategoryCreateData): Promise<IngredientCategoryAPI> {
    const response = await authFetch(`${API_BASE_URL}/ingredient-categories`, {
      method: 'POST',
      body: JSON.stringify(category)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear categoría de insumo');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar categoría de insumo
  async update(id: number, category: Partial<IngredientCategoryCreateData>): Promise<IngredientCategoryAPI> {
    const response = await authFetch(`${API_BASE_URL}/ingredient-categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(category)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar categoría de insumo');
    }

    const data = await response.json();
    return data.data;
  },

  // Toggle estado de categoría de insumo
  async toggle(id: number): Promise<IngredientCategoryAPI> {
    const response = await authFetch(`${API_BASE_URL}/ingredient-categories/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar categoría de insumo (soft delete)
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/ingredient-categories/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al eliminar categoría de insumo');
    }
  }
};

// ============================================
// UNIDADES DE MEDIDA (Units)
// ============================================

export interface UnitAPI {
  id: number;
  name: string;
  displayName: string;
  category: string;
  symbol: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UnitCreateData {
  name: string;
  displayName: string;
  category?: string;
  symbol?: string;
  sortOrder?: number;
}

export const unitsService = {
  // Obtener todas las unidades de medida
  async getAll(params?: { search?: string; isActive?: boolean | 'all'; category?: string }): Promise<UnitAPI[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.set('search', params.search);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive === 'all' ? 'all' : String(params.isActive));
    if (params?.category) queryParams.set('category', params.category);

    const url = `${API_BASE_URL}/units${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error('Error al obtener unidades de medida');
    }

    const data = await response.json();
    return data.data || [];
  },

  // Obtener una unidad por ID
  async getById(id: number): Promise<UnitAPI> {
    const response = await authFetch(`${API_BASE_URL}/units/${id}`);

    if (!response.ok) {
      throw new Error('Unidad de medida no encontrada');
    }

    const data = await response.json();
    return data.data;
  },

  // Crear nueva unidad de medida
  async create(unit: UnitCreateData): Promise<UnitAPI> {
    const response = await authFetch(`${API_BASE_URL}/units`, {
      method: 'POST',
      body: JSON.stringify(unit)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear unidad de medida');
    }

    const data = await response.json();
    return data.data;
  },

  // Actualizar unidad de medida
  async update(id: number, unit: Partial<UnitCreateData>): Promise<UnitAPI> {
    const response = await authFetch(`${API_BASE_URL}/units/${id}`, {
      method: 'PUT',
      body: JSON.stringify(unit)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al actualizar unidad de medida');
    }

    const data = await response.json();
    return data.data;
  },

  // Toggle estado de unidad de medida
  async toggle(id: number): Promise<UnitAPI> {
    const response = await authFetch(`${API_BASE_URL}/units/${id}/toggle`, {
      method: 'PATCH'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al cambiar estado');
    }

    const data = await response.json();
    return data.data;
  },

  // Eliminar unidad de medida (soft delete)
  async delete(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/units/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al eliminar unidad de medida');
    }
  },

  // Obtener categorías de unidades
  async getCategories(): Promise<{ value: string; label: string }[]> {
    const response = await authFetch(`${API_BASE_URL}/units/categories`);

    if (!response.ok) {
      throw new Error('Error al obtener categorías de unidades');
    }

    const data = await response.json();
    return data.data || [];
  }
};

// Export all services together
export const systemParametersService = {
  movementTypes: movementTypesService,
  presentations: presentationsService,
  productCategories: productCategoriesService,
  positions: positionsService,
  drivers: driversService,
  vehicles: vehiclesService,
  transportCompanies: transportCompaniesService,
  fiscalParameters: fiscalParametersService,
  ingredientCategories: ingredientCategoriesService,
  units: unitsService
};
